import React, { useState, useRef, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { VOICES, getVoice, setVoice, SPEEDS, getSpeed, setSpeed } from "../lib/settings";
import { api, API } from "../lib/api";
import { primeAudio } from "../lib/audio";
import { Check, Play, Loader2, Volume2, Gauge } from "lucide-react";
import { C } from "../lib/theme";

const SAMPLE = "Hi, I'm Pip. Let's learn something new together.";

export default function SettingsDialog({ open, onOpenChange }) {
  const [selected, setSelected] = useState(getVoice());
  const [speed, setSpeedState] = useState(getSpeed());
  const [previewing, setPreviewing] = useState(null);
  const audioRef = useRef(null);

  useEffect(() => { if (open) { setSelected(getVoice()); setSpeedState(getSpeed()); } }, [open]);
  useEffect(() => () => { if (audioRef.current) audioRef.current.pause(); }, []);

  const choose = (id) => { setSelected(id); setVoice(id); };
  const chooseSpeed = (s) => { setSpeedState(s); setSpeed(s); };

  const preview = async (id) => {
    if (audioRef.current) audioRef.current.pause();
    setPreviewing(id);
    try {
      let audio = audioRef.current;
      if (!audio) { audio = new Audio(); audioRef.current = audio; }
      primeAudio(audio); // unlock within the click gesture before the async fetch
      // Preview at the speed the user has selected, not a fixed 1x — otherwise
      // the speed buttons appear to do nothing.
      const { url } = await api.tts(SAMPLE, speed, id);
      audio.onended = () => setPreviewing(null);
      audio.onerror = () => setPreviewing(null);
      audio.src = `${API.replace(/\/api$/, "")}${url}`;
      audio.currentTime = 0;
      await audio.play();
    } catch (e) {
      setPreviewing(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md border border-hair bg-surface text-ink" data-testid="settings-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl text-ink">Settings</DialogTitle>
          <DialogDescription className="text-muted">Choose the voice Fathom uses when it reads lessons aloud.</DialogDescription>
        </DialogHeader>
        <div className="mt-1">
          <div className="mb-2 flex items-center gap-2 mono-label"><Volume2 className="h-3.5 w-3.5" /> READ-ALOUD VOICE</div>
          <div className="max-h-[50vh] space-y-1.5 overflow-y-auto pr-1">
            {VOICES.map((v) => {
              const active = selected === v.id;
              return (
                <div
                  key={v.id}
                  data-testid={`voice-option-${v.id}`}
                  onClick={() => choose(v.id)}
                  className="press flex cursor-pointer items-center justify-between rounded-lg border px-3 py-2.5"
                  style={{ borderColor: active ? C.blu : "rgba(255,255,255,0.08)", background: active ? `${C.blu}14` : "transparent" }}
                >
                  <div className="flex items-center gap-2">
                    <span className="flex h-5 w-5 items-center justify-center rounded-full border" style={{ borderColor: active ? C.blu : "rgba(255,255,255,0.2)" }}>
                      {active && <Check className="h-3 w-3" style={{ color: C.blu }} />}
                    </span>
                    <div>
                      <div className="text-sm font-medium">{v.label}</div>
                      <div className="text-xs text-muted">{v.desc}</div>
                    </div>
                  </div>
                  <button
                    data-testid={`voice-preview-${v.id}`}
                    onClick={(e) => { e.stopPropagation(); preview(v.id); }}
                    className="press inline-flex items-center gap-1 rounded-full border border-hair px-2.5 py-1 text-xs text-muted hover:text-ink"
                  >
                    {previewing === v.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Play className="h-3 w-3" />} Preview
                  </button>
                </div>
              );
            })}
          </div>
          <p className="mt-3 text-xs text-muted">Your voice choice is saved on this device and used everywhere Fathom reads to you.</p>

          <div className="my-4 h-px bg-white/10" />

          <div className="mb-2 flex items-center gap-2 mono-label"><Gauge className="h-3.5 w-3.5" /> DEFAULT READING SPEED</div>
          <div className="grid grid-cols-5 gap-2" data-testid="speed-options">
            {SPEEDS.map((s) => {
              const active = speed === s;
              return (
                <button
                  key={s}
                  data-testid={`speed-option-${s}`}
                  onClick={() => chooseSpeed(s)}
                  className="press rounded-md border py-2 font-mono text-sm"
                  style={{ borderColor: active ? C.blu : "rgba(255,255,255,0.08)", background: active ? `${C.blu}14` : "transparent", color: active ? C.blu : "#8B93A5" }}
                >
                  {s}x
                </button>
              );
            })}
          </div>
          <p className="mt-3 text-xs text-muted">New read-aloud plays start at this speed. You can still change speed per lesson.</p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
